import { useState } from "react";
import { nriValueStackData, nriWhatsappUrl, nriStripeUrl } from "../../data/nriTaxData";
import { FileText, Scale, FileCheck, Globe, Phone, ChevronDown, AlertCircle, ArrowRight } from "lucide-react";

const iconMap = { FileText, Scale, FileCheck, Globe, Phone };

const DeliverableRow = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  const Icon = iconMap[item.icon];
  return (
    <div className={`border transition-all duration-300 rounded-sm overflow-hidden ${open ? "border-[#3f4816]/30 bg-[#302f2c]/50" : "border-[#3f4816]/10 bg-[#1a1c1b] hover:border-[#3f4816]/25"}`}
      data-testid={`nri-deliverable-${index}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-4 px-5 py-4 text-left">
        {/* Number */}
        <span className="text-[#3f4816]/50 text-[10px] font-black flex-shrink-0 w-5" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>0{index + 1}</span>
        {/* Icon */}
        <div className="w-8 h-8 rounded-md bg-[#3f4816]/15 border border-[#3f4816]/25 flex items-center justify-center flex-shrink-0">
          {Icon && <Icon size={14} className="text-[#d9fb06]" />}
        </div>
        {/* Title */}
        <span className="text-[#dfddd6] font-bold text-sm flex-1" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{item.title}</span>
        {/* Value */}
        <span className="text-[#d9fb06] font-black text-sm flex-shrink-0 mr-3" style={{ fontFamily: "'Inter', Arial, sans-serif", letterSpacing: "-0.01em" }}>{item.value}</span>
        <ChevronDown size={15} className={`text-[#888680] flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-[#d9fb06]" : ""}`} />
      </button>
      <div className="overflow-hidden transition-all duration-300" style={{ maxHeight: open ? "300px" : "0", opacity: open ? 1 : 0 }}>
        <div className="px-5 pb-5 pl-[76px]">
          <p className="text-[#888680] text-xs leading-relaxed mb-3" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{item.body}</p>
          <div className="flex items-start gap-2 bg-[#252422] border border-[#3f4816]/15 rounded-sm px-3 py-2">
            <AlertCircle size={12} className="text-[#d9fb06]/60 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] leading-relaxed" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
              <span className="text-[#dfddd6] font-semibold">Why it matters: </span>
              <span className="text-[#888680]">{item.whyItMatters}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const NriValueStackSection = () => (
  <section id="nri-value" className="relative py-28" style={{ background: "#252422" }}>
    <div className="absolute top-0 left-0 right-0 h-px bg-[#3f4816]/30" />
    <div className="max-w-[87.5rem] mx-auto px-10">

      {/* Heading */}
      <div className="mb-14">
        <span className="inline-block text-[#888680] text-xs font-medium uppercase tracking-[0.2em] mb-5" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
          {nriValueStackData.sectionLabel}
        </span>
        <p className="text-[#888680]/60 font-semibold uppercase mb-1" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(0.85rem, 0.7rem + 0.6vw, 1rem)", letterSpacing: "-0.01em" }}>
          What's inside the
        </p>
        <h2 className="font-black uppercase leading-tight" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(1.8rem, 1.4rem + 2.5vw, 3.5rem)", letterSpacing: "-0.03em" }}>
          <span className="text-[#d9fb06]">NRI Tax</span>
          <br />
          <span className="text-[#dfddd6]">Position Report</span>
        </h2>
        <p className="text-[#888680] text-xs mt-2" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>Tap each item to see full details</p>
      </div>

      {/* Deliverables — accordion */}
      <div className="flex flex-col gap-2 mb-10 max-w-4xl">
        {nriValueStackData.deliverables.map((item, i) => <DeliverableRow key={i} item={item} index={i} />)}
      </div>

      {/* Total value — NOT the price */}
      <div className="max-w-4xl">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-[#1a1c1b] border border-[#d9fb06]/20 rounded-sm p-5">
          <div className="flex-1">
            <p className="text-[#888680] text-xs uppercase tracking-widest mb-1" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>Total Assigned Value</p>
            <p className="text-[#d9fb06] font-black" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(1.8rem, 1.5rem + 1vw, 2.5rem)", letterSpacing: "-0.03em" }}>
              <span className="line-through opacity-40">{nriValueStackData.totalValue}</span>
            </p>
            <p className="text-[#888680]/50 text-[11px] mt-1" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>Core work only — 5 bonuses on top</p>
          </div>
          <div className="flex flex-col items-start sm:items-end">
            <p className="text-[#888680]/50 text-[10px] uppercase tracking-widest mb-1" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>You pay</p>
            <p className="text-[#d9fb06] font-black" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(1.8rem, 1.5rem + 1vw, 2.5rem)", letterSpacing: "-0.03em" }}>₹14,999</p>
            <p className="text-[#888680]/50 text-[11px] mt-0.5" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>≈ $160 USD / 599 AED</p>
            <p className="text-[#25D366] text-[11px] font-semibold mb-3" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>Everything included</p>
            <a href={nriStripeUrl} target="_blank" rel="noopener noreferrer" data-testid="nri-value-cta"
              className="group inline-flex items-center gap-2 bg-[#d9fb06] text-[#1a1c1b] font-bold text-xs px-5 py-3 rounded-full transition-all duration-300 hover:scale-[1.02] hover:opacity-90 uppercase tracking-tight"
              style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
              Get My NRI Tax Report + Filing
              <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5 duration-300" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);
